import { tracking } from '@iwoca/frontend-tracking-library';
import { Pill } from '@iwoca/orion';

import {
  checkHasThirtyDayAndThreeMonthLimit,
  formatDuration,
} from './duration.helpers';
import { RefreshExpiredLimits } from './RefreshExpiredLimits';
import { SpendingLimitBar } from './SpendingLimitBar';
import { useGetBuyerSpendingLimit } from '../../../../../api/lending/lapiHooks';
import { useGetSpendingLimits } from '../../../hooks/useGetSpendingLimits';
import { formatCurrency } from '../../../utils/currency';

export const ExpiredLimits = ({
  setIsSpendingLimitDrawerOpen,
  refreshSpendingLimit,
  loading,
}: {
  setIsSpendingLimitDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  refreshSpendingLimit: () => void;
  loading: boolean;
}) => {
  const { spendingLimit, loadingSpendingLimit } = useGetBuyerSpendingLimit();
  const { spendingLimits } = useGetSpendingLimits();

  const activeSpendingLimit = spendingLimit?.data?.active_spending_limit;

  if (loadingSpendingLimit || !activeSpendingLimit) return null;

  const hasThirtyDayAndThreeMonthLimit = checkHasThirtyDayAndThreeMonthLimit(
    spendingLimits?.limits || [],
  );

  return (
    <div className="gap-y-6 max-sm:grid-cols-1 max-sm:gap-y-4 max-sm:[grid-template-areas:'left'_'bar'_'right'] grid grid-cols-[1fr_auto] [grid-template-areas:'left_right'_'bar_bar']">
      <div className="grid grid-cols-1 gap-s [grid-area:left]">
        <div className="gap-2 max-sm:text-xl flex flex-wrap items-center text-2xl font-reg">
          <div className="flex gap-s">
            <div>
              You have{' '}
              <span className="font-bold">
                {formatCurrency(activeSpendingLimit.available_amount)}
              </span>{' '}
              left to spend
            </div>
            <Pill size="small">Estimated</Pill>
          </div>
        </div>
        <div className="text-s font-reg">
          This is an estimated amount based on your last transaction with us
          &mdash; refresh it by clicking "Secure my limit" or make a purchase
          with Pay Later.
        </div>
      </div>
      <div className="max-sm:justify-self-start justify-self-end [grid-area:right]">
        <div className="gap-2 max-sm:justify-start max-sm:font-reg flex flex-wrap justify-end text-s font-med">
          <div>
            Estimated spending limit:{' '}
            {formatCurrency(activeSpendingLimit.max_amount)}
          </div>
          <RefreshExpiredLimits
            loading={loading}
            setIsSpendingLimitDrawerOpen={setIsSpendingLimitDrawerOpen}
            refreshSpendingLimit={refreshSpendingLimit}
          />
        </div>
        <div className="max-sm:text-left text-right text-xs font-reg">
          {formatDuration({
            duration: activeSpendingLimit.duration_in_months,
            groupSameLimits: hasThirtyDayAndThreeMonthLimit,
          })}{' '}
          purchases{' '}
          <button
            onClick={() => {
              tracking.ampli.spendingLimitViewedOtherTerms();
              setIsSpendingLimitDrawerOpen(true);
            }}
            className="ml-1 cursor-pointer border-none bg-none p-0 text-[color:var(--color-primary-40,#4b4bff)] underline"
          >
            Other terms
          </button>
        </div>
      </div>
      <div className="[grid-area:bar]">
        <SpendingLimitBar status="expired" />
      </div>
    </div>
  );
};
